import React, { useState, useEffect } from 'react';
import '../CSS/BlogList.css';
import { Link } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config/api';
import authService from '../services/authService';
import penIcon from '../assets/icons/penIcon.svg';
import trashIcon from '../assets/icons/trashIcon.svg';

// Spinner component
const Spinner = () => (
    <div className="spinner-container">
        <div className="d-flex align-items-center">
            <div className="spinner-border text-white me-3" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mb-0">Loading blog posts...</p>
        </div>
    </div>
);

const BlogList = ({ showActions = false, refreshTrigger = 0, showAllPosts = false, filterPublished = null }) => {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        fetchBlogPosts();
    }, [refreshTrigger]);

    // Retry function
    const retryFetch = () => {
        setRetryCount(0); // Reset retry count for manual retry
        setError(null);
        fetchBlogPosts();
    };

    const fetchBlogPosts = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log(`Attempting to fetch blog posts (attempt ${retryCount + 1}/3)`);
            
            const endpoint = showAllPosts ? API_CONFIG.ENDPOINTS.GET_ALL_POSTS_ADMIN : API_CONFIG.ENDPOINTS.GET_ALL_POSTS;
            const response = await fetch(buildApiUrl(endpoint), {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Blog posts fetched successfully:', data);
            
            let posts = data.records || [];
            
            // Filter posts based on published status if specified
            if (filterPublished !== null) {
                posts = posts.filter(post => post.published === filterPublished);
            }
            
            setBlogPosts(posts);
            setRetryCount(0); // Reset retry count on success
        } catch (err) {
            console.error('Error fetching blog posts:', err);
            
            // Auto-retry logic (max 3 attempts)
            if (retryCount < 2) {
                console.log(`Retrying... Attempt ${retryCount + 2}/3`);
                setTimeout(() => {
                    setRetryCount(prev => prev + 1);
                    fetchBlogPosts();
                }, 2000); // Wait 2 seconds before retry
                return;
            }
            
            // After 3 failed attempts, show error
            setError('Failed to load blog posts. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fi-FI', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
        });
    };

    const handleEdit = (postId) => {
        // Navigate to edit page
        console.log('Edit post:', postId);
        window.location.href = `/BlogWriting/${postId}`;
    };

    const handleDelete = async (postId) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                const response = await authService.deleteBlogPost(postId);
                
                if (response.ok) {
                    // Remove the deleted post from the list
                    setBlogPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
                    console.log('Post deleted successfully');
                } else {
                    const errorData = await response.json();
                    console.error('Failed to delete post:', errorData.message);
                    alert('Failed to delete post: ' + errorData.message);
                }
            } catch (error) {
                console.error('Error deleting post:', error);
                alert('Error deleting post: ' + error.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="blog-list-component">
                <Spinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="blog-list-component">
                <div className="error-section">
                    <h3 className="error-title">Error Loading Blog Posts</h3>
                    <p className="error-message">{error}</p>
                    <div className="button-group pt-4">
                        <button 
                            onClick={retryFetch} 
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            {loading ? 'Retrying...' : 'Retry'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (blogPosts.length === 0) {
        return (
            <div className="blog-list-component">
                <div className="empty-section">
                    <h3 className="empty-title">No Blog Posts Found</h3>
                    <p className="empty-message">There are currently no blog posts available.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-list-component">
            <ul className="blog-list">
                {blogPosts.map((post) => (
                    <li key={post.id} className="blog-item pb-4">
                        <div 
                            className="blog-link d-flex justify-content-between align-items-center"
                            onClick={() => window.location.href = `/BlogPost/${post.id}`}
                        >
                            <span>{post.title} - {formatDate(post.created_at)}</span>
                            {showActions && (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(post.id);
                                        }}
                                        title="Edit post"
                                    >
                                        <img src={penIcon} alt="Edit" className="action-icon" />
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(post.id);
                                        }}
                                        title="Delete post"
                                    >
                                        <img src={trashIcon} alt="Delete" className="action-icon" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default BlogList;